import { useState } from 'react'
import { X } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { SERVICE_AREAS } from './constants'
import { format } from 'date-fns'

// ── Pay-level derivation ──────────────────────────────────────────────────────
// A time entry's pay level is decided by the person's relationship to the client:
//   - they are that client's Account Director  -> 'ad'
//   - they are that client's Account Manager   -> 'am'
//   - otherwise (incl. no client)              -> their own profile role
// Stored on the row at save time so re-assigning a client's AD/AM later never
// rewrites past payroll. Shared by the manual-entry modal and the stopwatch so
// both apply the exact same rule.
export function derivePayLevel(userId, role, client) {
  if (client) {
    if (client.account_director_id === userId) return 'ad'
    if (client.account_manager_id === userId) return 'am'
  }
  return role
}

export const PAY_LEVEL_LABEL = {
  ad: 'AD level',
  am: 'AM level',
  technician: 'Technician',
  supervising_ad: 'Supervising AD',
}

export const CATEGORIES = [
  { value: 'task', label: 'Task' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'admin', label: 'Admin / no client' },
]

export const todayStr = () => format(new Date(), 'yyyy-MM-dd')

export function hm(seconds) {
  const s = seconds || 0
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

export const serviceAreaLabel = v => SERVICE_AREAS.find(a => a.value === v)?.label || v

// ISO timestamp -> 'HH:MM' for a <input type="time"> (local time)
export function toTimeInput(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  } catch { return '' }
}

// 'HH:MM' -> a friendly 'h:mm AM/PM' string, for read-only display of a draft's start time.
function fmtTimeInput(hhmm) {
  if (!hhmm) return ''
  const [h, m] = hhmm.split(':').map(Number)
  const d = new Date()
  d.setHours(h, m, 0, 0)
  try { return format(d, 'h:mm a') } catch { return hhmm }
}

// ── Manual entry modal (add / edit / draft / finish) ──────────────────────────
// A "draft" is a time_entries row with started_at set, ended_at still null, and
// is_manual_draft = true. It's the manual-entry counterpart to the stopwatch's
// in-flight row, and shares the same DB rule of "at most one in-flight row per
// user" — so a draft blocks starting the stopwatch and vice versa. Opening this
// modal on an existing draft (via the My Time list, the global timer bar, or the
// per-task control) lets the person either update it (still no end time — stays
// a draft) or finish it (fill in the end time, which computes the duration and
// closes it out).
export function TimeEntryModal({ allClients, tasks, currentUser, profile, editing, activeElsewhere, onClose, onSaved }) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [category, setCategory] = useState(editing?.category || 'task')
  const [clientId, setClientId] = useState(editing?.client_id || '')
  const [taskId, setTaskId] = useState(editing?.task_id || '')
  const [serviceArea, setServiceArea] = useState(editing?.service_area || '')
  const [date, setDate] = useState(editing?.entry_date || todayStr())
  const [startTime, setStartTime] = useState(toTimeInput(editing?.started_at))
  const [endTime, setEndTime] = useState(toTimeInput(editing?.ended_at))
  const [hours, setHours] = useState(editing ? String(Math.floor((editing.duration_seconds || 0) / 3600)) : '')
  const [minutes, setMinutes] = useState(editing ? String(Math.floor(((editing.duration_seconds || 0) % 3600) / 60)) : '')
  const [description, setDescription] = useState(editing?.description || '')

  // An "open draft" is the row we're editing, as long as it hasn't been finished yet.
  const isOpenDraft = !!(editing && editing.is_manual_draft && !editing.ended_at)

  const activeClients = allClients.filter(c => !c.archived_at)
  const selectedClient = allClients.find(c => c.id === clientId) || null
  const clientTasks = clientId ? tasks.filter(t => t.client_id === clientId) : []
  const selectedTask = taskId ? tasks.find(t => t.id === taskId) : null
  const isAdmin = category === 'admin'

  // If both start and end are given, duration is computed from them (and stored as timestamps).
  const usingTimes = !!(startTime && endTime)
  const timeDurationSec = usingTimes
    ? Math.round((new Date(`${date}T${endTime}:00`) - new Date(`${date}T${startTime}:00`)) / 1000)
    : null
  // Started but no end yet -> this save would be (or would remain) a draft.
  const draftIntent = !!startTime && !endTime

  const payLevel = derivePayLevel(currentUser.id, profile?.role, selectedClient)

  const onTaskChange = id => {
    setTaskId(id)
    const t = id ? tasks.find(x => x.id === id) : null
    if (t && t.service_area) setServiceArea(t.service_area)
  }

  const buildCommonPayload = () => ({
    user_id: currentUser.id,
    client_id: isAdmin ? (clientId || null) : clientId,
    task_id: isAdmin ? null : (taskId || null),
    project_id: selectedTask?.project_id || null,
    service_area: selectedTask?.service_area || serviceArea || null,
    category,
    pay_level: payLevel,
    entry_date: date,
    description: description.trim() || null,
  })

  // Save as a draft: only a start time is required. Leaves ended_at null (and
  // duration_seconds at 0 — the column is NOT NULL) so it keeps showing as
  // "in progress" until someone comes back and finishes it.
  const saveDraft = async () => {
    if (activeElsewhere) {
      toast(activeElsewhere.is_manual_draft
        ? "Finish your other draft entry first — only one can be open at a time"
        : 'Stop your running timer first — only one can be open at a time', 'error')
      return
    }
    if (!startTime) { toast('Enter a start time', 'error'); return }
    if (!isAdmin && !clientId) { toast('Select a client', 'error'); return }
    if (!profile?.role) { toast("Your profile role hasn't loaded — refresh and try again", 'error'); return }

    setSaving(true)
    const payload = {
      ...buildCommonPayload(),
      started_at: new Date(`${date}T${startTime}:00`).toISOString(),
      ended_at: null,
      duration_seconds: 0,
      is_manual_draft: true,
    }
    const { error } = editing
      ? await supabase.from('time_entries').update(payload).eq('id', editing.id)
      : await supabase.from('time_entries').insert(payload)

    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Saved as a draft — come back and add an end time to finish it', 'success')
    onSaved()
  }

  // Save a complete entry (has a duration one way or another), finishing a draft if editing one.
  const save = async () => {
    const duration = usingTimes
      ? timeDurationSec
      : (parseInt(hours, 10) || 0) * 3600 + (parseInt(minutes, 10) || 0) * 60
    if (usingTimes && (!duration || duration <= 0)) { toast('End time must be after start time', 'error'); return }
    if (!usingTimes && duration <= 0) { toast('Enter how long you worked', 'error'); return }
    if (!isAdmin && !clientId) { toast('Select a client', 'error'); return }
    if (!profile?.role) { toast("Your profile role hasn't loaded — refresh and try again", 'error'); return }

    // If we're finishing an open draft but the person typed hours/minutes instead of
    // an exact end time, derive a real ended_at from the draft's started_at + duration
    // (rather than stamping "now", which could be long after they actually stopped).
    const isFinishingDraftByDuration = !usingTimes && isOpenDraft

    setSaving(true)
    const payload = {
      ...buildCommonPayload(),
      started_at: usingTimes
        ? new Date(`${date}T${startTime}:00`).toISOString()
        : (isFinishingDraftByDuration ? editing.started_at : null),
      ended_at: usingTimes
        ? new Date(`${date}T${endTime}:00`).toISOString()
        : (isFinishingDraftByDuration ? new Date(Date.parse(editing.started_at) + duration * 1000).toISOString() : null),
      duration_seconds: duration,
    }

    const { error } = editing
      ? await supabase.from('time_entries').update(payload).eq('id', editing.id)
      : await supabase.from('time_entries').insert(payload)

    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast(isOpenDraft ? 'Draft finished — time logged' : (editing ? 'Time updated' : 'Time logged'), 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">
            {isOpenDraft ? 'Finish draft entry' : (editing ? 'Edit time entry' : 'Log time')}
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body">
          {isOpenDraft && (
            <div style={{
              marginBottom: 12, padding: '8px 12px', borderRadius: 'var(--radius)',
              background: 'var(--gold-light)', color: 'var(--text-secondary)', fontSize: 13,
            }}>
              Started at <strong>{fmtTimeInput(toTimeInput(editing.started_at))}</strong> and still open. Add an end
              time to finish it, or just update the details below to keep it as a draft.
            </div>
          )}

          {activeElsewhere && !isOpenDraft && (
            <div style={{
              marginBottom: 12, padding: '8px 12px', borderRadius: 'var(--radius)',
              background: 'var(--red-bg)', color: 'var(--text-secondary)', fontSize: 13,
            }}>
              {activeElsewhere.is_manual_draft
                ? 'You already have an open draft entry. Finish it before starting another.'
                : 'You have a timer running. Stop it before starting a draft.'}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-control" value={category} onChange={e => setCategory(e.target.value)}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="form-control" type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Client {isAdmin
                ? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional — keep admin time minimal)</span>
                : <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>*</span>}
            </label>
            <select className="form-control" value={clientId} onChange={e => { setClientId(e.target.value); setTaskId('') }}>
              <option value="">— Select client —</option>
              {activeClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {clientId && (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                Logged at: <strong style={{ color: 'var(--text-secondary)' }}>{PAY_LEVEL_LABEL[payLevel] || payLevel}</strong>
              </div>
            )}
          </div>

          {!isAdmin && clientId && (
            <div className="form-group">
              <label className="form-label">Task <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
              <select className="form-control" value={taskId} onChange={e => onTaskChange(e.target.value)}>
                <option value="">— No specific task —</option>
                {clientTasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Service area {selectedTask && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(from task)</span>}</label>
            <select className="form-control" value={serviceArea} onChange={e => setServiceArea(e.target.value)} disabled={!!selectedTask}>
              <option value="">— None —</option>
              {SERVICE_AREAS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start time <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
              <input className="form-control" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">
                End time <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
                {isOpenDraft && (
                  <button
                    type="button"
                    onClick={() => setEndTime(toTimeInput(new Date().toISOString()))}
                    style={{ marginLeft: 8, fontWeight: 400, fontSize: 12, color: 'var(--teal)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Set to now
                  </button>
                )}
              </label>
              <input className="form-control" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
            </div>
          </div>
          {usingTimes && (
            <div style={{ marginTop: -6, marginBottom: 4, fontSize: 12, color: 'var(--text-muted)' }}>
              {timeDurationSec > 0
                ? <>Duration from start/end: <strong style={{ color: 'var(--text-secondary)' }}>{hm(timeDurationSec)}</strong></>
                : <span style={{ color: 'var(--red)' }}>End time must be after start time</span>}
            </div>
          )}
          {draftIntent && (
            <div style={{ marginTop: -6, marginBottom: 4, fontSize: 12, color: 'var(--text-muted)' }}>
              No end time yet — use <strong>Save as draft</strong> below and come back to finish it once you know when you stopped.
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Hours</label>
              <input className="form-control" type="number" min="0" value={usingTimes ? '' : hours} onChange={e => setHours(e.target.value)} placeholder="0" disabled={usingTimes || draftIntent} />
            </div>
            <div className="form-group">
              <label className="form-label">Minutes</label>
              <input className="form-control" type="number" min="0" max="59" value={usingTimes ? '' : minutes} onChange={e => setMinutes(e.target.value)} placeholder="0" disabled={usingTimes || draftIntent} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
            <textarea className="form-control" rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="What did you work on?" />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          {draftIntent && (
            <button className="btn btn-ghost" style={{ border: '1px solid var(--border)' }} onClick={saveDraft} disabled={saving || (!!activeElsewhere && !isOpenDraft)}>
              {saving ? 'Saving…' : (isOpenDraft ? 'Update draft' : 'Save as draft')}
            </button>
          )}
          <button className="btn btn-primary" onClick={save} disabled={saving || draftIntent} title={draftIntent ? 'Add an end time, or use Save as draft' : undefined}>
            {saving ? 'Saving…' : (isOpenDraft ? 'Finish entry' : (editing ? 'Save changes' : 'Log time'))}
          </button>
        </div>
      </div>
    </div>
  )
}